import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FacilityID } from "../../../constants/defaultKeys";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import { getAppointmentListApi } from "../../../services/receptionist.services";
import useQuery from "../../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../../helpers/urlFilters";
import btnStyles from "../../../styles/Custom/Button.module.css";
import AppointmentsTable from "./AppointmentsTable";
import AppointsModal from "./AppointsModal";
import { PlusOutlined } from "@ant-design/icons";
import { Button, DatePicker, Space } from "antd";
import dayjs from "dayjs";
import useIsMobile from "../../../hooks/useIsMobile";
import useReceptionistMobilePickerProps from "../receptionistMobilePickerProps";
import { UiDataTable } from "../../ui";
import styles from "./Appointments.module.css";

const { RangePicker } = DatePicker;
const dateFormat = "YYYY-MM-DD";

const Appointments = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const page = parsePageParam(params);
  const searchQuery = parseSearchParam(params);
  const isMobile = useIsMobile();
  const mobilePickerProps = useReceptionistMobilePickerProps();
  const { userDetails } = useContext(UserContext);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);

  const startDateStr = params?.start_date || dayjs().format(dateFormat);
  const endDateStr = params?.end_date || dayjs().format(dateFormat);
  const startDate = dayjs(startDateStr);
  const endDate = dayjs(endDateStr);

  const setPage = (newPage) => {
    updateUrlSearchParams(navigate, { page: newPage });
  };

  const refreshPage = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    let queryParams = {
      page: page,
      facility: Number(getCookie(FacilityID)) || userDetails?.currFacility,
      start_date: startDate.format(dateFormat),
      end_date: endDate.format(dateFormat),
    };
    if (searchQuery) {
      queryParams.search = searchQuery;
      queryParams.page = 1;
    }
    const queryString = new URLSearchParams(queryParams);
    setLoading(true);
    getAppointmentListApi(queryString)
      .then((res) => {
        setData(res?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [
    startDateStr,
    endDateStr,
    refresh,
    searchQuery,
    page,
    Number(getCookie(FacilityID)) || userDetails?.currFacility,
  ]);

  const onContactSearch = (e) => {
    updateUrlSearchParams(navigate, {
      search: e?.target?.value || null,
      page: 1,
    });
  };

  const toolbarTrailing = (
    <Space
      wrap
      className={styles.receptionAppointmentsActions}
      size={isMobile ? "small" : "middle"}
    >
      <RangePicker
        allowClear={false}
        className={styles.receptionAppointmentsDateRange}
        style={{ background: "#fff" }}
        value={[startDate, endDate]}
        onChange={(dates) => {
          if (dates?.[0] && dates?.[1]) {
            updateUrlSearchParams(navigate, {
              start_date: dates[0].format(dateFormat),
              end_date: dates[1].format(dateFormat),
              page: 1,
            });
          }
        }}
        {...(isMobile ? mobilePickerProps : {})}
      />
      <Button
        type="primary"
        className={`${btnStyles.mdBtn} ${styles.receptionAppointmentsAddBtn}`}
        onClick={() => setOpen(true)}
      >
        <PlusOutlined /> Appointments
      </Button>
    </Space>
  );

  return (
    <div className={styles.receptionAppointments}>
      <UiDataTable
        showHeader={false}
        shellClassName="ui-table-fill-shell"
        onSearch={onContactSearch}
        searchValue={searchQuery || ""}
        searchPlaceholder="Search for appointments"
        toolbarTrailing={toolbarTrailing}
      >
        <AppointmentsTable
          loading={loading}
          data={data}
          refresh={refreshPage}
          page={page}
          setPage={setPage}
        />
      </UiDataTable>

      {open && (
        <AppointsModal open={open} setOpen={setOpen} refresh={refreshPage} />
      )}
    </div>
  );
};

export default Appointments;
